import { faPlusCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Form, useFormikContext } from "formik";
import { Button, OverlayTrigger, Table, Tooltip } from "react-bootstrap";
import { useTheme } from "../../contexts/ThemeContext";
import { Loader } from "../common/progress/Loader";
import { useParentProducts } from "./contexts/ParentProductsContext";
import { useParentProductsActions } from "./hooks/useParentProductsActions";
import { ParentProductDetails } from "./models/ParentProductDetails";
import { ParentProductsTableEditableRow } from "./ParentProductsTableEditableRow";
import { ParentProductsTableReadonlyRow } from "./ParentProductsTableReadonlyRow";
import { parentProductValidationSchema } from "./schemas/ParentProductValidationSchema";
import { useEffect } from "react";
import { usePreserve } from "../../contexts/PreserveContext";
import { useLocalization } from "../../contexts/LocalizationContext";
import { TableFooter } from "../common/TableFooter";

const defaultCapacity = 10;

export const ParentProductsTable = () => {
  const parents = useParentProducts();
  const formik = useFormikContext<ParentProductDetails>();
  const { theme, buttonStyle } = useTheme();
  const { isLoading, handleUpdate, handleCreate, handleDelete } = useParentProductsActions();
  const { values, initialValues, setTouched } = formik;
  const { editModeId, parentProducts, capacity } = parents;
  const { onCapacityChange, onEditModeIdChange } = parents;
  const { safeExecute, setCurrentState, setInitialState } = usePreserve();
  const { translate } = useLocalization();

  useEffect(() => {
    setCurrentState(values);
  }, [values]);

  useEffect(() => {
    setInitialState(initialValues);
  }, [initialValues]);

  const handleAdd = () => safeExecute(() => onEditModeIdChange(0));

  const handleClick = async () => {
    try {
      await parentProductValidationSchema(translate).validate(values, { abortEarly: false });
      setTouched({});
      if (editModeId === 0) await handleCreate(values);
      else await handleUpdate(editModeId, values);
    } catch (errors) {
      setTouched({ productName: true, supportedFileExtensionsIds: true });
    }
  };

  if (isLoading) return <Loader />;

  const newParentProducts = parentProducts.slice(0, capacity);

  return (
    <Form>
      <Table striped bordered>
        <thead>
          <tr className="d-none d-xl-table-row">
            <th>{translate("plugins.id")}</th>
            <th>{translate("plugins.productName")}</th>
            <th>{translate("plugins.supportedFileExtensions")}</th>
            <th colSpan={2} className="text-center">
              <OverlayTrigger overlay={<Tooltip>{translate("common.addNew")}</Tooltip>}>
                <Button
                  variant="transparent"
                  className="p-0"
                  aria-label={translate("common.addNew")}
                  onClick={handleAdd}
                >
                  <FontAwesomeIcon icon={faPlusCircle} style={{ color: theme.backgroundColor }} />
                </Button>
              </OverlayTrigger>
            </th>
          </tr>
        </thead>
        <tbody>
          {newParentProducts.map((x, i) =>
            x.id === editModeId ? (
              <ParentProductsTableEditableRow key={x.id} index={i + 1} handleSave={handleClick} />
            ) : (
              <ParentProductsTableReadonlyRow key={x.id} item={x} index={i + 1} onDelete={() => handleDelete(x.id)} />
            )
          )}
          {editModeId === 0 && (
            <ParentProductsTableEditableRow index={parentProducts.length + 1} handleSave={handleClick} />
          )}
          <TableFooter
            expand={() => onCapacityChange(defaultCapacity)}
            expandAll={() => onCapacityChange(parentProducts.length)}
            capacity={defaultCapacity}
            showMenu={!!newParentProducts.length && parentProducts.length > defaultCapacity}
            showEmpty={!newParentProducts.length && editModeId === -1}
            colSpan={5}
            emptyMessage={translate("plugins.noProductsFound")}
          />
        </tbody>
      </Table>
      <Button style={buttonStyle} onClick={handleAdd}>
        {translate("common.addNew")}
      </Button>
    </Form>
  );
};
